import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import CtaBlock from "@/components/seo/CtaBlock";
import useSeo from "@/hooks/useSeo";
import { indianCities, internationalCities } from "@/data/seo/cities";
import { breadcrumbSchema, canonicalFor } from "@/lib/seoHelpers";

const CityCard = ({ city }) => (
  <Link
    to={`/locations/${city.slug}`}
    className="group p-5 transition-all hover:bg-white/5 flex items-center justify-between gap-4"
    style={{
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(212,175,55,0.15)",
    }}
    data-testid={`locations-index-link-${city.slug}`}
  >
    <div className="flex items-center gap-4">
      <MapPin size={16} className="text-[var(--iw-gold)] shrink-0" />
      <div>
        <h3
          className="font-serif-display text-[var(--iw-ivory)] text-xl leading-tight group-hover:text-[var(--iw-gold)] transition-colors"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {city.city}
        </h3>
        <span className="overline text-[var(--iw-faint)] block mt-0.5 text-[10px]">
          {city.region}
        </span>
      </div>
    </div>
    <ArrowUpRight
      size={16}
      className="text-[var(--iw-faint)] group-hover:text-[var(--iw-gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0"
    />
  </Link>
);

export default function LocationsIndex() {
  const canonical = canonicalFor("/locations");
  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Locations", href: "/locations" },
  ];

  useSeo({
    title:
      "Wedding Invitations Across India & Worldwide | Cities We Serve | InvitationWala",
    description:
      "InvitationWala designs premium digital wedding invitations for couples across India and internationally — Mumbai, Delhi, Dubai, London, NYC & more.",
    canonical,
  });

  return (
    <SeoPageLayout schemas={[breadcrumbSchema(crumbs)]}>
      <div
        data-testid="locations-index"
        className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-14 md:mb-20">
          <span className="overline text-[var(--iw-gold)]">Locations</span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Wedding Invitations Across the World
          </motion.h1>
          <p className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mt-8 max-w-2xl">
            We design wedding invitations for couples across India and the
            Indian diaspora worldwide. Every city page includes locally-tuned
            typography, motif references and cultural sensitivity.
          </p>
        </div>

        {/* India Cities */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              India · {indianCities().length} Cities
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {indianCities().map((c) => (
              <CityCard key={c.slug} city={c} />
            ))}
          </div>
        </section>

        {/* International Cities */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Worldwide · {internationalCities().length} Cities
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {internationalCities().map((c) => (
              <CityCard key={c.slug} city={c} />
            ))}
          </div>
        </section>

        <CtaBlock
          title="Your city not listed?"
          subtitle="We serve couples worldwide via WhatsApp. Message us with your wedding location and we'll design accordingly."
          testid="locations-index-cta"
        />
      </div>
    </SeoPageLayout>
  );
}
